import * as THREE from 'three';
import { smooth, type Random } from './critter-math.js';
import { restingAction, type BirdAction } from './bird-model.js';
import type { BirdSpecies } from './bird-species.js';

/**
 * A bird on the wing along a path someone else defines (a flyover track, a flock wheeling over a lantern). Given three
 * successive points along the path it banks into the turns, pitches with the climb, and alternates bursts of flapping with
 * glides, the glides longer the more the species rides the air.
 */
export class PathFlight {
  readonly action: BirdAction = restingAction();
  heading = 0;
  private bank = 0; private time = 0;
  private readonly phase: number;

  constructor(private species: BirdSpecies, random: Random) {
    this.phase = random() * Math.PI * 2; this.action.idle = random() * 40;
  }

  follow(dt: number, here: THREE.Vector3, ahead: THREE.Vector3, later: THREE.Vector3) {
    const a = this.action;
    this.time += dt;
    const nowHeading = Math.atan2(ahead.x - here.x, ahead.z - here.z), nextHeading = Math.atan2(later.x - ahead.x, later.z - ahead.z);
    this.heading = nowHeading;
    const turn = Math.atan2(Math.sin(nextHeading - nowHeading), Math.cos(nextHeading - nowHeading));
    const climb = Math.atan2(ahead.y - here.y, Math.hypot(ahead.x - here.x, ahead.z - here.z));
    this.bank += (THREE.MathUtils.clamp(turn * 2.2, -.7, .7) - this.bank) * (1 - Math.exp(-dt * 5));
    // `glide` is near 1 for the quiet half of each rhythm.
    const glide = smooth(Math.sin(this.time * .5 + this.phase) * 1.6);
    a.air = 1; a.crouch = 0; a.flare = 0; a.walk = 0; a.bank = this.bank; a.pitch = climb * .8;
    a.effort = 1 - Math.min(.95, this.species.glide * 1.4) * glide;
    a.flap += dt * (this.species.flapRate[0] + this.species.flapRate[1] * a.effort * .6); a.idle += dt;
  }
}
